/**
 * FAQ - Expandable question cards
 *
 * 5 questions with toggle expand. Smooth height animation via CSS transition.
 * Copy routes through landing.faq.* i18n keys (locale from ?lang / ?ref hint /
 * navigator.languages — see LandingI18n.tsx). v11: q4 (app download — answered
 * by the hero's dual CTA) and q6 (device sync — folded into the loop story)
 * dropped; the "한국어 · 繁體中文 · 日本語" line keeps native glyphs.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { useLandingT } from './landing-locale'
import type { StringKey } from '@shared/i18n'

const QUESTIONS: { q: StringKey; a: StringKey }[] = [
  { q: 'landing.faq.q1', a: 'landing.faq.a1' },
  { q: 'landing.faq.q2', a: 'landing.faq.a2' },
  { q: 'landing.faq.q3', a: 'landing.faq.a3' },
  { q: 'landing.faq.q5', a: 'landing.faq.a5' },
  { q: 'landing.faq.q7', a: 'landing.faq.a7' },
  { q: 'landing.faq.q8', a: 'landing.faq.a8' },
  { q: 'landing.faq.q9', a: 'landing.faq.a9' },
]

function FAQItem({ item, isOpen, onToggle, index }: {
  item: { q: string; a: string }
  isOpen: boolean
  onToggle: () => void
  index: number
}) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (contentRef.current) {
      const h = contentRef.current.scrollHeight
      setHeight(h)
    }
  }, [isOpen])

  return (
    <div
      className={`reveal reveal-stagger-${index + 1} bg-surface rounded-2xl overflow-hidden`}
    >
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <span className="font-semibold text-sm text-on-surface pr-4">{item.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-muted-soft shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>
      <div
        style={{ maxHeight: isOpen ? height : 0 }}
        className="transition-[max-height] duration-200 ease-in-out overflow-hidden"
      >
        <div ref={contentRef} className="px-6 pb-4">
          <p className="text-on-surface-variant text-sm leading-relaxed">{item.a}</p>
        </div>
      </div>
    </div>
  )
}

export function FAQ() {
  // v10-B6: default-expand the first question (free/grant) — the three
  // conversion-critical answers were buried behind collapsed state.
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const revealRef = useScrollReveal()
  const t = useLandingT()

  const toggle = useCallback((i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i))
  }, [])

  return (
    <section id="faq" className="py-16 md:py-24 bg-surface-container-low">
      <div ref={revealRef} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-12">
          {t('landing.faq.heading')}
        </h2>

        <div className="space-y-3">
          {QUESTIONS.map((item, i) => (
            <FAQItem
              key={i}
              item={{ q: t(item.q), a: t(item.a) }}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
              index={i}
            />
          ))}
        </div>

        <p className="reveal text-center text-sm text-on-surface-variant mt-10">
          {t('landing.faq.still')}{' '}
          <a
            href="mailto:support@piccard.app"
            className="text-primary font-semibold hover:underline"
          >
            support@piccard.app
          </a>
        </p>
      </div>
    </section>
  )
}
